import React from 'react';
import Sectiontitle from '../section-title';
import { useSiteSettings, resolveMediaUrl } from '../../siteSettings';
import './style.css';

const Gallery = () => {
    const settings = useSiteSettings();
    const section = settings.gallerySection;
    const items = section && Array.isArray(section.items) ? section.items.filter((item) => item && item.image) : [];
    const [activeIndex, setActiveIndex] = React.useState(null);
    const [isMobile, setIsMobile] = React.useState(false);
    const [mobileIndex, setMobileIndex] = React.useState(0);
    const mobileAutoplayPausedRef = React.useRef(false);
    const mobileResumeTimerRef = React.useRef(null);
    const mobileTouchRef = React.useRef({ startX: 0, startY: 0, isHorizontalSwipe: false });
    const suppressMobileClickRef = React.useRef(false);
    const lightboxTouchRef = React.useRef({ startX: 0, startY: 0, isHorizontalSwipe: false });

    const pauseMobileAutoplay = React.useCallback(() => {
        mobileAutoplayPausedRef.current = true;
        if (mobileResumeTimerRef.current) {
            window.clearTimeout(mobileResumeTimerRef.current);
            mobileResumeTimerRef.current = null;
        }
    }, []);

    const resumeMobileAutoplay = React.useCallback(() => {
        if (mobileResumeTimerRef.current) window.clearTimeout(mobileResumeTimerRef.current);
        mobileResumeTimerRef.current = window.setTimeout(() => {
            mobileAutoplayPausedRef.current = false;
            mobileResumeTimerRef.current = null;
        }, 5000);
    }, []);

    React.useEffect(() => {
        const mediaQuery = window.matchMedia('(max-width: 767px)');
        const updateMobileState = () => setIsMobile(mediaQuery.matches);
        updateMobileState();
        if (mediaQuery.addEventListener) mediaQuery.addEventListener('change', updateMobileState);
        else mediaQuery.addListener(updateMobileState);
        return () => {
            if (mediaQuery.removeEventListener) mediaQuery.removeEventListener('change', updateMobileState);
            else mediaQuery.removeListener(updateMobileState);
        };
    }, []);

    React.useEffect(() => {
        if (!isMobile || items.length < 2) return undefined;
        const timer = window.setInterval(() => {
            if (!mobileAutoplayPausedRef.current && activeIndex === null) {
                setMobileIndex((current) => (current + 1) % items.length);
            }
        }, 4000);
        return () => {
            window.clearInterval(timer);
            if (mobileResumeTimerRef.current) window.clearTimeout(mobileResumeTimerRef.current);
        };
    }, [activeIndex, isMobile, items.length]);

    React.useEffect(() => {
        if (mobileIndex >= items.length) setMobileIndex(0);
    }, [mobileIndex, items.length]);

    React.useEffect(() => {
        if (activeIndex === null) return undefined;
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') setActiveIndex(null);
            if (event.key === 'ArrowLeft') setActiveIndex((current) => (current - 1 + items.length) % items.length);
            if (event.key === 'ArrowRight') setActiveIndex((current) => (current + 1) % items.length);
        };
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [activeIndex, items.length]);

    if (!items.length) return null;

    const showPrevious = () => setActiveIndex((current) => (current - 1 + items.length) % items.length);
    const showNext = () => setActiveIndex((current) => (current + 1) % items.length);
    const handleMobileTouchStart = (event) => {
        if (!isMobile || items.length < 2 || !event.touches[0]) return;
        pauseMobileAutoplay();
        mobileTouchRef.current = {
            startX: event.touches[0].clientX,
            startY: event.touches[0].clientY,
            isHorizontalSwipe: false,
        };
    };
    const handleMobileTouchMove = (event) => {
        if (!event.touches[0]) return;
        const touch = mobileTouchRef.current;
        const deltaX = event.touches[0].clientX - touch.startX;
        const deltaY = event.touches[0].clientY - touch.startY;
        if (Math.abs(deltaX) > 10 && Math.abs(deltaX) > Math.abs(deltaY)) {
            touch.isHorizontalSwipe = true;
            event.preventDefault();
        }
    };
    const handleMobileTouchEnd = (event) => {
        const touch = mobileTouchRef.current;
        const changedTouch = event.changedTouches && event.changedTouches[0];
        if (!changedTouch) {
            resumeMobileAutoplay();
            return;
        }
        const deltaX = changedTouch.clientX - touch.startX;
        const deltaY = changedTouch.clientY - touch.startY;
        if (touch.isHorizontalSwipe && Math.abs(deltaX) >= 50 && Math.abs(deltaX) > Math.abs(deltaY)) {
            setMobileIndex((current) => (current + (deltaX < 0 ? 1 : -1) + items.length) % items.length);
            suppressMobileClickRef.current = true;
        }
        mobileTouchRef.current = { startX: 0, startY: 0, isHorizontalSwipe: false };
        resumeMobileAutoplay();
    };
    const handleMobileCardClick = (index, event) => {
        if (suppressMobileClickRef.current) {
            event.preventDefault();
            suppressMobileClickRef.current = false;
            return;
        }
        setActiveIndex(index);
    };
    const handleLightboxTouchStart = (event) => {
        if (!event.touches[0]) return;
        lightboxTouchRef.current = {
            startX: event.touches[0].clientX,
            startY: event.touches[0].clientY,
            isHorizontalSwipe: false,
        };
    };
    const handleLightboxTouchMove = (event) => {
        if (!event.touches[0]) return;
        const touch = lightboxTouchRef.current;
        const deltaX = event.touches[0].clientX - touch.startX;
        const deltaY = event.touches[0].clientY - touch.startY;
        if (Math.abs(deltaX) > 10 && Math.abs(deltaX) > Math.abs(deltaY)) {
            touch.isHorizontalSwipe = true;
            event.preventDefault();
        }
    };
    const handleLightboxTouchEnd = (event) => {
        const touch = lightboxTouchRef.current;
        const changedTouch = event.changedTouches && event.changedTouches[0];
        if (changedTouch) {
            const deltaX = changedTouch.clientX - touch.startX;
            const deltaY = changedTouch.clientY - touch.startY;
            if (touch.isHorizontalSwipe && Math.abs(deltaX) >= 50 && Math.abs(deltaX) > Math.abs(deltaY)) {
                if (deltaX < 0) showNext();
                else showPrevious();
            }
        }
        lightboxTouchRef.current = { startX: 0, startY: 0, isHorizontalSwipe: false };
    };
    const safeMobileIndex = Math.min(mobileIndex, items.length - 1);
    const visibleItems = isMobile ? [{ item: items[safeMobileIndex], index: safeMobileIndex }] : items.map((item, index) => ({ item, index }));

    return (
        <section id="gallery" className="gallery-section section-padding">
            <Sectiontitle section={(section && section.title) || 'Galerie'} />
            <div className="container gallery-mobile-viewport" onTouchStart={handleMobileTouchStart} onTouchMove={handleMobileTouchMove} onTouchEnd={handleMobileTouchEnd} onTouchCancel={handleMobileTouchEnd}><div className="row gallery-grid">
                {visibleItems.map(({ item, index }) => <div className="col-lg-4 col-md-6 col-12 gallery-grid-item" key={`${item.image}-${index}`}><button type="button" className="gallery-card" onClick={(event) => handleMobileCardClick(index, event)} aria-label={`Ouvrir l’image ${index + 1} sur ${items.length}`}><img src={resolveMediaUrl(item.image)} alt={`Galerie ${index + 1}`} draggable="false" /></button></div>)}
            </div>{isMobile && items.length > 1 && <div className="gallery-mobile-dots" aria-label="Navigation de la galerie">{items.map((item, index) => <button type="button" className={index === mobileIndex ? 'is-active' : ''} onClick={() => { pauseMobileAutoplay(); setMobileIndex(index); resumeMobileAutoplay(); }} aria-label={`Afficher l’image ${index + 1}`} key={`gallery-dot-${index}`} />)}</div>}</div>
            {activeIndex !== null && <div className="gallery-lightbox" role="dialog" aria-modal="true" aria-label={`Image ${activeIndex + 1} sur ${items.length}`} onClick={() => setActiveIndex(null)}><button type="button" className="gallery-lightbox-close" onClick={() => setActiveIndex(null)} aria-label="Fermer">×</button><button type="button" className="gallery-lightbox-arrow gallery-lightbox-prev" onClick={(event) => { event.stopPropagation(); showPrevious(); }} aria-label="Image précédente"><i className="fa fa-chevron-left" /></button><div className="gallery-lightbox-content" onClick={(event) => event.stopPropagation()} onTouchStart={handleLightboxTouchStart} onTouchMove={handleLightboxTouchMove} onTouchEnd={handleLightboxTouchEnd} onTouchCancel={handleLightboxTouchEnd}><img src={resolveMediaUrl(items[activeIndex].image)} alt={`Galerie ${activeIndex + 1}`} draggable="false" /><div className="gallery-lightbox-counter">{activeIndex + 1} / {items.length}</div></div><button type="button" className="gallery-lightbox-arrow gallery-lightbox-next" onClick={(event) => { event.stopPropagation(); showNext(); }} aria-label="Image suivante"><i className="fa fa-chevron-right" /></button></div>}
        </section>
    );
};

export default Gallery;
